import { Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { ArrowRight } from 'lucide-react'
import { breadcrumbList, faqPage, jsonLdScript } from '@/lib/schema'
import { clampDescription, firstThatFits } from '@/lib/meta'

export interface HttpStatusData {
  code: number
  name: string
  description: string
  causes: string[]
  fixes: string[]
  monitoringTip: string
}

function codeColor(code: number): string {
  if (code >= 200 && code < 300) return 'text-status-up'
  if (code >= 300 && code < 400) return 'text-primary'
  if (code >= 400 && code < 500) return 'text-status-degraded'
  return 'text-status-down'
}

function Section({ children }: { children: React.ReactNode }) {
  return (
    <section className="border-t border-border">
      <div className="container mx-auto max-w-3xl px-4 py-12 md:py-16">{children}</div>
    </section>
  )
}

function Steps({ items }: { items: string[] }) {
  return (
    <ol className="divide-y divide-border border-y border-border">
      {items.map((item, i) => (
        <li key={i} className="flex gap-4 py-4">
          <span className="w-6 shrink-0 num text-sm text-muted-foreground">{i + 1}</span>
          <p className="leading-relaxed text-body">{item}</p>
        </li>
      ))}
    </ol>
  )
}

export function HttpStatusPage({ data }: { data: HttpStatusData }) {
  return (
    <>
      <section>
        <div className="container mx-auto px-4 py-12 md:py-16">
          <div className="max-w-3xl">
            <p className={`num text-6xl font-semibold tracking-tight md:text-7xl ${codeColor(data.code)}`}>
              {data.code}
            </p>
            <h1 className="mt-4 text-[2.25rem] font-semibold leading-[1.1] tracking-tight md:text-[3rem]">{data.name}</h1>
            <p className="mt-5 max-w-2xl text-lead text-body">{data.description}</p>
          </div>
        </div>
      </section>

      <Section>
        <h2 className="mb-4 text-[1.5rem] font-semibold leading-tight tracking-tight">What does HTTP {data.code} mean?</h2>
        <p className="text-base leading-relaxed text-body">{data.description}</p>
      </Section>

      <Section>
        <h2 className="mb-6 text-[1.5rem] font-semibold leading-tight tracking-tight">Common causes</h2>
        <Steps items={data.causes} />
      </Section>

      <Section>
        <h2 className="mb-6 text-[1.5rem] font-semibold leading-tight tracking-tight">How to fix it</h2>
        <Steps items={data.fixes} />
      </Section>

      <Section>
        <h2 className="mb-4 text-[1.5rem] font-semibold leading-tight tracking-tight">Monitor for HTTP {data.code} errors</h2>
        <p className="text-base leading-relaxed text-body">{data.monitoringTip}</p>
      </Section>

      <Section>
        <h2 className="text-[1.75rem] font-semibold leading-tight tracking-tight md:text-headline">Catch HTTP errors before your users do</h2>
        <p className="mt-3 max-w-lg text-lead text-body">
          Start free, or choose a plan, for 30-second monitoring and alert confirmation.
        </p>
        <div className="mt-6">
          <Button size="lg" className="h-11 px-5 text-[15px]" asChild>
            <Link to="/pricing">
              See plans
              <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
            </Link>
          </Button>
          <p className="mt-3 text-sm text-muted-foreground">from $3/mo · 30-day money-back guarantee</p>
        </div>
      </Section>
    </>
  )
}

export function httpStatusHead(data: HttpStatusData) {
  const title = firstThatFits([
    `HTTP ${data.code} ${data.name} — What It Means & How to Fix It`,
    `HTTP ${data.code} ${data.name} — Meaning & Fix`,
    `HTTP ${data.code} ${data.name}`,
  ])
  const description = clampDescription(
    data.description.length < 110 ? `${data.description} See common causes and how to check it.` : data.description,
  )
  return {
    meta: [
      { title },
      { name: 'description', content: description },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:image', content: 'https://uptrack.app/og/http-code.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: `https://uptrack.app/http/${data.code}` },
    ],
    scripts: [
      jsonLdScript(breadcrumbList([
        { name: 'Home', url: 'https://uptrack.app' },
        { name: 'HTTP Status Codes', url: 'https://uptrack.app/http' },
        { name: `${data.code} ${data.name}`, url: `https://uptrack.app/http/${data.code}` },
      ])),
      jsonLdScript(faqPage([
        { question: `What does HTTP ${data.code} ${data.name} mean?`, answer: data.description },
        { question: `What are the common causes of HTTP ${data.code}?`, answer: data.causes.join(' ') },
        { question: `How do I fix HTTP ${data.code} errors?`, answer: data.fixes.join(' ') },
        { question: `How do I monitor for HTTP ${data.code} errors?`, answer: data.monitoringTip },
      ])),
    ],
  }
}
